import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { Plane, ExternalLink } from 'lucide-react'
import type { BookingBrief } from '../../../shared/types/chat'
import type { FeedbackType } from '../types'
import { DataRow, AnimatedChevron } from '@tendbot/ui'
import InlineFeedback from './InlineFeedback'
import { expandCollapse } from '@tendbot/ui'

interface BookingBriefCardProps {
  bookingBrief: BookingBrief
  messageId: string
}

export default function BookingBriefCard({ bookingBrief, messageId }: BookingBriefCardProps) {
  const { t } = useTranslation('chat')
  const [expanded, setExpanded] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass-card rounded-xl overflow-hidden border-l-4 border-gold bg-gold/5"
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <Plane size={14} className="text-gold" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gold">{t('booking.header')}</span>
        </div>

        {/* Recommended option */}
        <div className="mb-3">
          <h4 className="text-ivory font-medium text-sm mb-1">
            {bookingBrief.recommended.provider} {bookingBrief.recommended.flight}
          </h4>
          <p className="text-pearl/60 text-xs">
            {bookingBrief.recommended.route} · {bookingBrief.recommended.outbound.date}
          </p>
          <div className="mt-2 space-y-1.5">
            <DataRow label={t('booking.outbound')} value={`${bookingBrief.recommended.outbound.departure} → ${bookingBrief.recommended.outbound.arrival} (${bookingBrief.recommended.outbound.duration})`} />
            {bookingBrief.recommended.return && (
              <DataRow label={t('booking.return')} value={`${bookingBrief.recommended.return.departure} → ${bookingBrief.recommended.return.arrival} (${bookingBrief.recommended.return.duration})`} />
            )}
            <DataRow label={t('booking.class')} value={bookingBrief.recommended.class} valueClassName="text-gold" />
            <DataRow label={t('booking.price')} value={`${bookingBrief.recommended.price.toLocaleString()} ${bookingBrief.recommended.currency}`} valueClassName="text-gold font-semibold" />
            {bookingBrief.recommended.loyaltyPoints && (
              <DataRow label={t('booking.pointsEarned')} value={bookingBrief.recommended.loyaltyPoints} />
            )}
          </div>
        </div>

        {/* Reasoning */}
        <div className="p-2.5 rounded-lg bg-gold/5 border border-gold/10 mb-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-pearl/70 italic flex-1">"{bookingBrief.reasoning}"</p>
            <InlineFeedback
              id={`${messageId}-reasoning`}
              onFeedback={(id: string, fb: FeedbackType) => console.log('Feedback:', id, fb)}
            />
          </div>
        </div>

        {/* Alternatives - expandable */}
        {bookingBrief.alternatives && bookingBrief.alternatives.length > 0 && (
          <div className="mb-3">
            <button
              onClick={() => setExpanded(prev => !prev)}
              className="flex items-center gap-1 text-xs text-pearl/60 hover:text-gold/60 transition-colors"
            >
              <AnimatedChevron isOpen={expanded} size={12} className="text-pearl/60" />
              {expanded ? t('booking.hideAlternatives') : t('booking.showAlternatives')} {t('booking.alternatives', { count: bookingBrief.alternatives.length })}
            </button>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  {...expandCollapse}
                  className="overflow-hidden"
                >
                  <div className="mt-2 space-y-2">
                    {bookingBrief.alternatives.map((alt, i) => (
                      <div key={i} className="p-2.5 rounded-lg bg-charcoal/50 space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-pearl">{alt.airline} {alt.flight}</span>
                          <span className="text-pearl font-medium">{alt.price.toLocaleString()} {alt.currency}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-pearl/50">{alt.route} · {alt.departure} → {alt.arrival}</span>
                          <span className="text-pearl/50">{alt.class}</span>
                        </div>
                        {alt.note && (
                          <p className="text-xs text-pearl/50 italic">{alt.note}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Book button (external link) */}
        <a
          href={bookingBrief.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-2.5 px-4 rounded-full bg-gold text-midnight text-sm font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2"
        >
          <ExternalLink size={14} />
          {bookingBrief.bookingCta}
        </a>

        {/* Follow-up note */}
        {bookingBrief.followUp && (
          <p className="text-xs text-pearl/50 text-center mt-2">{bookingBrief.followUp}</p>
        )}
      </div>
    </motion.div>
  )
}
